import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useResponsiveTickInterval } from "@/lib/chart-utils";
import { format, parseISO, addDays, differenceInDays } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { TrendingUp, AlertTriangle, Info, RefreshCw, Calendar, Filter, X, Grid3X3 } from "lucide-react";
import { useSystemSnapshots, useMultiLocationSnapshots, useCapacityScheduleSettings, type CapacitySnapshot } from "@/hooks/use-capacity-snapshots";
import { useInsightsContext } from "@/contexts/insights-context";
import { BreakdownTrendChart, type TrendMetric } from "./population-breakdowns";
import { KennelStatusTrendsChart } from "./kennel-status-trends-chart";
import { AdoptionPipelineChart } from "./adoption-pipeline-chart";

const MAX_LOCATIONS = 4;

const SERIES_COLORS = {
  system: "hsl(var(--primary))",
  locations: [
    "hsl(142, 71%, 45%)",  // Green - distinct from blue system line
    "hsl(25, 95%, 53%)",   // Orange
    "hsl(280, 65%, 60%)",  // Purple
    "hsl(var(--chart-4))", // Chart 4 color
  ],
};

interface SeriesDataPoint {
  utilizationPct: number | null;
  occupancyWithLimits: number | null;
  capacityWithLimits: number | null;
  occupancyAllKennels: number | null;
  dataAsOf: string | null;
  hasData: boolean;
  occupancyDogs: number | null;
  occupancyCats: number | null;
  occupancyOther: number | null;
}

interface MultiSeriesDataPoint {
  date: string;
  formattedDate: string;
  system?: SeriesDataPoint;
  [locationCode: string]: SeriesDataPoint | string | undefined;
}

function formatSnapshotDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), "M/d/yy");
  } catch {
    return dateStr;
  }
}

function formatFullDate(dateStr: string | null): string {
  if (!dateStr) return "Unknown";
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

function buildMultiSeriesData(
  systemSnapshots: CapacitySnapshot[] | undefined,
  locationSnapshots: Record<string, CapacitySnapshot[]>,
  days: number,
  showSystem: boolean,
  selectedLocations: string[]
): MultiSeriesDataPoint[] {
  const allDates = new Set<string>();
  const snapshotMaps: Record<string, Map<string, CapacitySnapshot>> = {};

  if (showSystem && systemSnapshots) {
    snapshotMaps["system"] = new Map();
    for (const s of systemSnapshots) {
      snapshotMaps["system"].set(s.snapshotDate, s);
      allDates.add(s.snapshotDate);
    }
  }

  for (const loc of selectedLocations) {
    if (locationSnapshots[loc]) {
      snapshotMaps[loc] = new Map();
      for (const s of locationSnapshots[loc]) {
        snapshotMaps[loc].set(s.snapshotDate, s);
        allDates.add(s.snapshotDate);
      }
    }
  }

  if (allDates.size === 0) return [];

  const sortedDates = Array.from(allDates).sort();
  const endDate = parseISO(sortedDates[sortedDates.length - 1]);
  const startDate = addDays(endDate, -(days - 1));

  const result: MultiSeriesDataPoint[] = [];
  let currentDate = startDate;

  while (differenceInDays(endDate, currentDate) >= 0) {
    const dateStr = format(currentDate, "yyyy-MM-dd");
    const point: MultiSeriesDataPoint = {
      date: dateStr,
      formattedDate: formatSnapshotDate(dateStr),
    };

    if (showSystem && snapshotMaps["system"]) {
      const snapshot = snapshotMaps["system"].get(dateStr);
      point.system = snapshot
        ? {
            utilizationPct: snapshot.utilizationPct,
            occupancyWithLimits: snapshot.occupancyWithLimits,
            capacityWithLimits: snapshot.capacityWithLimits,
            occupancyAllKennels: snapshot.occupancyAllKennels,
            dataAsOf: snapshot.dataAsOf,
            hasData: true,
            occupancyDogs: snapshot.occupancyDogs,
            occupancyCats: snapshot.occupancyCats,
            occupancyOther: snapshot.occupancyOther,
          }
        : {
            utilizationPct: null,
            occupancyWithLimits: null,
            capacityWithLimits: null,
            occupancyAllKennels: null,
            dataAsOf: null,
            hasData: false,
            occupancyDogs: null,
            occupancyCats: null,
            occupancyOther: null,
          };
    }

    for (const loc of selectedLocations) {
      if (snapshotMaps[loc]) {
        const snapshot = snapshotMaps[loc].get(dateStr);
        point[loc] = snapshot
          ? {
              utilizationPct: snapshot.utilizationPct,
              occupancyWithLimits: snapshot.occupancyWithLimits,
              capacityWithLimits: snapshot.capacityWithLimits,
              occupancyAllKennels: snapshot.occupancyAllKennels,
              dataAsOf: snapshot.dataAsOf,
              hasData: true,
              occupancyDogs: snapshot.occupancyDogs,
              occupancyCats: snapshot.occupancyCats,
              occupancyOther: snapshot.occupancyOther,
            }
          : {
              utilizationPct: null,
              occupancyWithLimits: null,
              capacityWithLimits: null,
              occupancyAllKennels: null,
              dataAsOf: null,
              hasData: false,
              occupancyDogs: null,
              occupancyCats: null,
              occupancyOther: null,
            };
      }
    }

    result.push(point);
    currentDate = addDays(currentDate, 1);
  }

  return result;
}

// Build chart data with comparison (previous period) series
// The previous period data is time-shifted to align with current period dates
function buildComparisonSeriesData(
  systemSnapshots: CapacitySnapshot[] | undefined,
  locationSnapshots: Record<string, CapacitySnapshot[]>,
  days: number,
  showSystem: boolean,
  selectedLocations: string[],
  comparisonDays: number
): MultiSeriesDataPoint[] {
  const allDates = new Set<string>();
  const snapshotMaps: Record<string, Map<string, CapacitySnapshot>> = {};

  // Build maps for current period data
  if (showSystem && systemSnapshots) {
    snapshotMaps["system"] = new Map();
    for (const s of systemSnapshots) {
      snapshotMaps["system"].set(s.snapshotDate, s);
      allDates.add(s.snapshotDate);
    }
  }

  for (const loc of selectedLocations) {
    if (locationSnapshots[loc]) {
      snapshotMaps[loc] = new Map();
      for (const s of locationSnapshots[loc]) {
        snapshotMaps[loc].set(s.snapshotDate, s);
        allDates.add(s.snapshotDate);
      }
    }
  }

  if (allDates.size === 0) return [];

  const sortedDates = Array.from(allDates).sort();
  const endDate = parseISO(sortedDates[sortedDates.length - 1]);
  const startDate = addDays(endDate, -(days - 1));

  const result: MultiSeriesDataPoint[] = [];
  let currentDate = startDate;

  while (differenceInDays(endDate, currentDate) >= 0) {
    const dateStr = format(currentDate, "yyyy-MM-dd");
    // Calculate the corresponding date in the previous period (shifted back by comparisonDays)
    const prevPeriodDate = addDays(currentDate, -comparisonDays);
    const prevDateStr = format(prevPeriodDate, "yyyy-MM-dd");
    
    const point: MultiSeriesDataPoint = {
      date: dateStr,
      formattedDate: formatSnapshotDate(dateStr),
    };

    // Current period system data
    if (showSystem && snapshotMaps["system"]) {
      const snapshot = snapshotMaps["system"].get(dateStr);
      point.system = snapshot
        ? {
            utilizationPct: snapshot.utilizationPct,
            occupancyWithLimits: snapshot.occupancyWithLimits,
            capacityWithLimits: snapshot.capacityWithLimits,
            occupancyAllKennels: snapshot.occupancyAllKennels,
            dataAsOf: snapshot.dataAsOf,
            hasData: true,
            occupancyDogs: snapshot.occupancyDogs,
            occupancyCats: snapshot.occupancyCats,
            occupancyOther: snapshot.occupancyOther,
          }
        : {
            utilizationPct: null,
            occupancyWithLimits: null,
            capacityWithLimits: null,
            occupancyAllKennels: null,
            dataAsOf: null,
            hasData: false,
            occupancyDogs: null,
            occupancyCats: null,
            occupancyOther: null,
          };
      
      // Previous period system data (shifted to align with current dates)
      const prevSnapshot = snapshotMaps["system"].get(prevDateStr);
      point["system_prev"] = prevSnapshot
        ? {
            utilizationPct: prevSnapshot.utilizationPct,
            occupancyWithLimits: prevSnapshot.occupancyWithLimits,
            capacityWithLimits: prevSnapshot.capacityWithLimits,
            occupancyAllKennels: prevSnapshot.occupancyAllKennels,
            dataAsOf: prevSnapshot.dataAsOf,
            hasData: true,
            occupancyDogs: prevSnapshot.occupancyDogs,
            occupancyCats: prevSnapshot.occupancyCats,
            occupancyOther: prevSnapshot.occupancyOther,
          }
        : {
            utilizationPct: null,
            occupancyWithLimits: null,
            capacityWithLimits: null,
            occupancyAllKennels: null,
            dataAsOf: null,
            hasData: false,
            occupancyDogs: null,
            occupancyCats: null,
            occupancyOther: null,
          };
    }

    // Current and previous period location data
    for (const loc of selectedLocations) {
      if (snapshotMaps[loc]) {
        const snapshot = snapshotMaps[loc].get(dateStr);
        point[loc] = snapshot
          ? {
              utilizationPct: snapshot.utilizationPct,
              occupancyWithLimits: snapshot.occupancyWithLimits,
              capacityWithLimits: snapshot.capacityWithLimits,
              occupancyAllKennels: snapshot.occupancyAllKennels,
              dataAsOf: snapshot.dataAsOf,
              hasData: true,
              occupancyDogs: snapshot.occupancyDogs,
              occupancyCats: snapshot.occupancyCats,
              occupancyOther: snapshot.occupancyOther,
            }
          : {
              utilizationPct: null,
              occupancyWithLimits: null,
              capacityWithLimits: null,
              occupancyAllKennels: null,
              dataAsOf: null,
              hasData: false,
              occupancyDogs: null,
              occupancyCats: null,
              occupancyOther: null,
            };
        
        // Previous period location data
        const prevSnapshot = snapshotMaps[loc].get(prevDateStr);
        point[`${loc}_prev`] = prevSnapshot
          ? {
              utilizationPct: prevSnapshot.utilizationPct,
              occupancyWithLimits: prevSnapshot.occupancyWithLimits,
              capacityWithLimits: prevSnapshot.capacityWithLimits,
              occupancyAllKennels: prevSnapshot.occupancyAllKennels,
              dataAsOf: prevSnapshot.dataAsOf,
              hasData: true,
              occupancyDogs: prevSnapshot.occupancyDogs,
              occupancyCats: prevSnapshot.occupancyCats,
              occupancyOther: prevSnapshot.occupancyOther,
            }
          : {
              utilizationPct: null,
              occupancyWithLimits: null,
              capacityWithLimits: null,
              occupancyAllKennels: null,
              dataAsOf: null,
              hasData: false,
              occupancyDogs: null,
              occupancyCats: null,
              occupancyOther: null,
            };
      }
    }

    result.push(point);
    currentDate = addDays(currentDate, 1);
  }

  return result;
}

interface SeriesConfig {
  key: string;
  label: string;
  color: string;
  strokeWidth: number;
  strokeDasharray?: string;
  isPreviousPeriod?: boolean;
}

interface MultiSeriesTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: MultiSeriesDataPoint; dataKey: string; value: number | null; stroke: string }>;
  seriesConfigs: SeriesConfig[];
  showOccupancy?: boolean;
}

function CapacityTooltip({ active, payload, seriesConfigs, showOccupancy = false }: MultiSeriesTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;
  const hasAnyData = seriesConfigs.some((config) => {
    const seriesData = data[config.key] as SeriesDataPoint | undefined;
    return seriesData?.hasData;
  });

  if (!hasAnyData) {
    return (
      <div className="rounded-lg border bg-background p-3 shadow-lg text-sm">
        <p className="font-medium mb-2">{formatFullDate(data.date)}</p>
        <p className="text-muted-foreground italic">No snapshot data for this date</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-background p-3 shadow-lg text-sm min-w-[200px]">
      <p className="font-medium mb-2">{formatFullDate(data.date)}</p>
      <div className="space-y-2">
        {seriesConfigs.map((config) => {
          const seriesData = data[config.key] as SeriesDataPoint | undefined;

          if (!seriesData?.hasData) {
            return (
              <div key={config.key} className="flex items-center gap-2 text-muted-foreground">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                <span className="flex-1">{config.label}</span>
                <span className="italic text-xs">No data</span>
              </div>
            );
          }

          return (
            <div key={config.key} className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                <span className="font-medium">{config.label}</span>
              </div>
              <div className="pl-5 space-y-0.5 text-xs">
                <p className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Utilization:</span>
                  <span className="font-medium">
                    {seriesData.utilizationPct !== null ? `${seriesData.utilizationPct}%` : "N/A"}
                  </span>
                </p>
                {showOccupancy && (
                  <p className="flex justify-between gap-4">
                    <span className="text-muted-foreground">Occupancy:</span>
                    <span className="font-medium">
                      {seriesData.occupancyWithLimits !== null && seriesData.capacityWithLimits !== null 
                        ? `${seriesData.occupancyWithLimits} / ${seriesData.capacityWithLimits}`
                        : "N/A"}
                    </span>
                  </p>
                )}
                {seriesData.dataAsOf && (
                  <p className="text-muted-foreground">
                    Data as of: {formatFullDate(seriesData.dataAsOf)}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PopulationTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: MultiSeriesDataPoint; dataKey: string; value: number | null; stroke: string }>;
  seriesConfigs: SeriesConfig[];
}

function PopulationTooltip({ active, payload, seriesConfigs }: PopulationTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;
  const hasAnyData = seriesConfigs.some((config) => {
    const seriesData = data[config.key] as SeriesDataPoint | undefined;
    return seriesData?.hasData;
  });

  if (!hasAnyData) {
    return (
      <div className="rounded-lg border bg-background p-3 shadow-lg text-sm">
        <p className="font-medium mb-2">{formatFullDate(data.date)}</p>
        <p className="text-muted-foreground italic">No snapshot data for this date</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-background p-3 shadow-lg text-sm min-w-[220px]">
      <p className="font-medium mb-2">{formatFullDate(data.date)}</p>
      <div className="space-y-3">
        {seriesConfigs.map((config) => {
          const seriesData = data[config.key] as SeriesDataPoint | undefined;

          if (!seriesData?.hasData) {
            return (
              <div key={config.key} className="flex items-center gap-2 text-muted-foreground">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                <span className="flex-1">{config.label}</span>
                <span className="italic text-xs">No data</span>
              </div>
            );
          }

          const hasSpeciesData = seriesData.occupancyDogs !== null || 
                                  seriesData.occupancyCats !== null || 
                                  seriesData.occupancyOther !== null;

          return (
            <div key={config.key}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                  <span className="text-sm font-medium">{config.label}</span>
                </div>
                <span className="font-semibold">
                  {seriesData.occupancyAllKennels !== null ? seriesData.occupancyAllKennels.toLocaleString() : "N/A"}
                </span>
              </div>
              {hasSpeciesData && (
                <div className="ml-5 mt-1 text-xs text-muted-foreground space-y-0.5">
                  <div className="flex justify-between gap-2">
                    <span>Dogs</span>
                    <span>{seriesData.occupancyDogs?.toLocaleString() ?? 0}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span>Cats</span>
                    <span>{seriesData.occupancyCats?.toLocaleString() ?? 0}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span>Other</span>
                    <span>{seriesData.occupancyOther?.toLocaleString() ?? 0}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface CapacityChartProps {
  data: MultiSeriesDataPoint[];
  seriesConfigs: SeriesConfig[];
  showOccupancy: boolean;
  height?: number;
}

function TrendsLegend({ seriesConfigs }: { seriesConfigs: SeriesConfig[] }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-3 text-xs text-muted-foreground">
      {seriesConfigs.map((config) => (
        <div key={config.key} className="flex items-center gap-1.5">
          <div
            className="h-0.5 w-4 rounded"
            style={{
              backgroundColor: config.color,
              ...(config.strokeDasharray && { borderTop: `2px dashed ${config.color}`, backgroundColor: "transparent" }),
            }}
          />
          <span>{config.label}</span>
        </div>
      ))}
    </div>
  );
}

function CapacityChart({ data, seriesConfigs, showOccupancy, height = 280 }: CapacityChartProps) {
  const hasAnyData = data.some((d) =>
    seriesConfigs.some((config) => {
      const seriesData = d[config.key] as SeriesDataPoint | undefined;
      return seriesData?.hasData;
    })
  );

  const tickInterval = useResponsiveTickInterval(data.length);

  if (!hasAnyData) {
    return (
      <div className="flex items-center justify-center h-[280px] border rounded-lg bg-muted/20">
        <div className="text-center">
          <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No population data available</p>
          <p className="text-sm text-muted-foreground mt-1">Select System-wide or locations to view trends</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 5, right: showOccupancy ? 60 : 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="formattedDate"
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ className: "stroke-muted" }}
          interval={tickInterval}
        />
        <YAxis
          yAxisId="left"
          domain={[0, 100]}
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ className: "stroke-muted" }}
          tickFormatter={(value) => `${value}%`}
        />
        <RechartsTooltip
          content={<CapacityTooltip seriesConfigs={seriesConfigs} showOccupancy={showOccupancy} />}
        />
        <ReferenceLine
          yAxisId="left"
          y={85}
          stroke="hsl(var(--destructive))"
          strokeDasharray="5 5"
          strokeOpacity={0.5}
        />
        {seriesConfigs.map((config) => (
          <Line
            key={config.key}
            type="monotone"
            dataKey={(d: MultiSeriesDataPoint) => {
              const seriesData = d[config.key] as SeriesDataPoint | undefined;
              return seriesData?.utilizationPct ?? null;
            }}
            name={config.label}
            stroke={config.color}
            strokeWidth={config.strokeWidth}
            strokeDasharray={config.strokeDasharray}
            dot={{ fill: config.color, strokeWidth: 0, r: config.key === "system" ? 3 : 2 }}
            activeDot={{ r: 5 }}
            connectNulls={false}
            yAxisId="left"
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

interface PopulationChartProps {
  data: MultiSeriesDataPoint[];
  seriesConfigs: SeriesConfig[];
  height?: number;
}

function PopulationChart({ data, seriesConfigs, height = 280 }: PopulationChartProps) {
  const hasAnyData = data.some((d) =>
    seriesConfigs.some((config) => {
      const seriesData = d[config.key] as SeriesDataPoint | undefined;
      return seriesData?.hasData;
    })
  );

  const tickInterval = useResponsiveTickInterval(data.length);

  const maxValue = useMemo(() => {
    let max = 0;
    for (const d of data) {
      for (const config of seriesConfigs) {
        const seriesData = d[config.key] as SeriesDataPoint | undefined;
        if (seriesData?.occupancyAllKennels && seriesData.occupancyAllKennels > max) {
          max = seriesData.occupancyAllKennels;
        }
      }
    }
    return Math.ceil(max * 1.1);
  }, [data, seriesConfigs]);

  if (!hasAnyData) {
    return (
      <div className="flex items-center justify-center h-[280px] border rounded-lg bg-muted/20">
        <div className="text-center">
          <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No population data available</p>
          <p className="text-sm text-muted-foreground mt-1">Select System-wide or locations to view trends</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="formattedDate"
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ className: "stroke-muted" }}
          interval={tickInterval}
        />
        <YAxis
          domain={[0, maxValue || 100]}
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ className: "stroke-muted" }}
          tickFormatter={(value) => value.toLocaleString()}
        />
        <RechartsTooltip
          content={<PopulationTooltip seriesConfigs={seriesConfigs} />}
        />
        {seriesConfigs.map((config) => (
          <Line
            key={config.key}
            type="monotone"
            dataKey={(d: MultiSeriesDataPoint) => {
              const seriesData = d[config.key] as SeriesDataPoint | undefined;
              return seriesData?.occupancyAllKennels ?? null;
            }}
            name={config.label}
            stroke={config.color}
            strokeWidth={config.strokeWidth}
            strokeDasharray={config.strokeDasharray}
            dot={{ fill: config.color, strokeWidth: 0, r: config.key === "system" ? 3 : 2 }}
            activeDot={{ r: 5 }}
            connectNulls={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

const DAYS_PRESETS = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
];

type ComparisonMode = 'none' | 'wow' | 'mom';

const COMPARISON_OPTIONS: { value: ComparisonMode; label: string; fullLabel: string; days: number }[] = [
  { value: 'none', label: 'None', fullLabel: 'None', days: 0 },
  { value: 'wow', label: 'Wk/Wk', fullLabel: 'Week over Week', days: 7 },
  { value: 'mom', label: 'Mo/Mo', fullLabel: 'Month over Month', days: 30 },
];

const MIN_DAYS = 1;
const MAX_DAYS = 365;

// Component for side-by-side breakdown chart comparison
interface BreakdownComparisonGridProps {
  showSystem: boolean;
  systemSnapshots: CapacitySnapshot[];
  selectedLocations: string[];
  locationSnapshotsMap: Record<string, CapacitySnapshot[]>;
  getLocationLabel: (code: string) => string;
  metric: TrendMetric;
  days: number;
  isLoading: boolean;
}

function BreakdownComparisonGrid({
  showSystem,
  systemSnapshots,
  selectedLocations,
  locationSnapshotsMap,
  getLocationLabel,
  metric,
  days,
  isLoading,
}: BreakdownComparisonGridProps) {
  // Build list of charts to show
  const charts: Array<{ id: string; label: string; snapshots: CapacitySnapshot[] }> = [];
  
  if (showSystem) {
    charts.push({ id: 'system', label: 'System-wide', snapshots: systemSnapshots });
  }
  
  selectedLocations.forEach(locCode => {
    const snapshots = locationSnapshotsMap[locCode] ?? [];
    charts.push({ id: locCode, label: getLocationLabel(locCode), snapshots });
  });
  
  // If nothing selected, default to system
  if (charts.length === 0) {
    charts.push({ id: 'system', label: 'System-wide', snapshots: systemSnapshots });
  }
  
  // Single chart - full width
  if (charts.length === 1) {
    return (
      <div>
        <div className="text-sm font-medium text-muted-foreground mb-2">{charts[0].label}</div>
        <BreakdownTrendChart 
          snapshots={charts[0].snapshots} 
          metric={metric} 
          days={days} 
          isLoading={isLoading} 
        />
      </div>
    );
  }
  
  // Determine grid columns based on count - max 2 columns to keep charts readable
  const gridCols = 'grid-cols-1 md:grid-cols-2';
  
  return (
    <div className={`grid ${gridCols} gap-6`}>
      {charts.map(chart => (
        <div key={chart.id} className="min-w-0">
          <div className="text-sm font-medium mb-2 flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ 
                backgroundColor: chart.id === 'system' 
                  ? SERIES_COLORS.system 
                  : SERIES_COLORS.locations[selectedLocations.indexOf(chart.id) % SERIES_COLORS.locations.length]
              }}
            />
            {chart.label}
          </div>
          <BreakdownTrendChart 
            snapshots={chart.snapshots} 
            metric={metric} 
            days={days} 
            isLoading={isLoading}
            compact={charts.length > 1}
          />
        </div>
      ))}
    </div>
  );
}

interface CapacityTrendsPanelProps {
  locations: Array<{ code: string; label: string }>;
  /** Optional: Page-level selected location to sync with (clicking location cards) */
  syncWithLocation?: string | null;
  /** Whether there is stale data */
  isStale?: boolean;
  /** Names of stale locations for tooltip */
  staleLocationNames?: string[];
  /** Controlled mode: externally managed days value */
  controlledDays?: number;
  /** When true, hides the internal date range selector (controlled by parent) */
  hideDateControls?: boolean;
  /** Optional render callback to inject hint blocks above each chart section */
  renderHint?: (sectionKey: string) => React.ReactNode;
}

export function CapacityTrendsPanel({ locations, syncWithLocation, isStale = false, staleLocationNames = [], controlledDays, hideDateControls = false, renderHint }: CapacityTrendsPanelProps) {
  const { selectedShelter } = useInsightsContext();
  const { data: scheduleSettings } = useCapacityScheduleSettings();
  
  const [internalDays, setInternalDays] = useState(30);
  const days = controlledDays ?? internalDays;
  const setDays = (d: number) => { if (controlledDays === undefined) setInternalDays(d); };
  const [customDaysInput, setCustomDaysInput] = useState("");
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [showOccupancy, setShowOccupancy] = useState(false);
  const [showSystem, setShowSystem] = useState(true);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('none');
  const [breakdownMetric, setBreakdownMetric] = useState<TrendMetric>("species");

  const trendsCardRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [stickyTop, setStickyTop] = useState(0);

  useEffect(() => {
    const scrollContainer = document.querySelector("[data-insights-scroll-container]");
    const nav = scrollContainer?.querySelector("[data-testid='nav-insights-sections']") as HTMLElement | null;
    if (!nav) return;
    const measure = () => setStickyTop(nav.offsetHeight + 8);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);
  
  // Track the last navigation target to detect actual navigation changes
  const lastNavTarget = useRef<string | null>(null);
  
  // Sync with InsightsContext sidebar navigation
  // Navigation always resets panel to match the sidebar selection
  useEffect(() => {
    // Only sync when sidebar selection actually changes (not on re-renders)
    if (lastNavTarget.current === selectedShelter) {
      return;
    }
    lastNavTarget.current = selectedShelter;
    
    if (selectedShelter === "system") {
      setShowSystem(true);
      setSelectedLocations([]);
      setComparisonMode('none');
    } else {
      setShowSystem(false);
      setSelectedLocations([selectedShelter]);
    }
  }, [selectedShelter]);
  
  // Track the last card navigation target
  const lastCardTarget = useRef<string | null>(null);
  
  // Sync with page-level location selection (when user clicks location summary cards)
  useEffect(() => {
    if (!syncWithLocation) {
      lastCardTarget.current = null;
      return;
    }
    
    // Only sync when card selection actually changes
    if (lastCardTarget.current === syncWithLocation) {
      return;
    }
    lastCardTarget.current = syncWithLocation;
    
    setShowSystem(false);
    setSelectedLocations([syncWithLocation]);
  }, [syncWithLocation]);

  const handlePresetChange = (value: string) => {
    if (value === "custom") {
      setIsCustomMode(true);
      setCustomDaysInput(String(days));
    } else {
      setIsCustomMode(false);
      setDays(Number(value));
    }
  };

  const handleCustomDaysChange = (value: string) => {
    setCustomDaysInput(value);
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= MIN_DAYS && num <= MAX_DAYS) {
      setDays(num);
    }
  };

  const toggleLocation = useCallback((locationCode: string) => {
    setSelectedLocations((prev) => {
      if (prev.includes(locationCode)) {
        return prev.filter((l) => l !== locationCode);
      }
      if (prev.length >= MAX_LOCATIONS) {
        return prev;
      }
      return [...prev, locationCode];
    });
  }, []);


  const showSystemOnly = useCallback(() => {
    setShowSystem(true);
    setSelectedLocations([]);
  }, []);

  const customDaysNum = parseInt(customDaysInput, 10);
  const isCustomDaysValid =
    !isCustomMode || (!isNaN(customDaysNum) && customDaysNum >= MIN_DAYS && customDaysNum <= MAX_DAYS);
  const customDaysError = isCustomMode && customDaysInput !== "" && !isCustomDaysValid;
  const currentSelectValue = isCustomMode
    ? "custom"
    : DAYS_PRESETS.find((p) => p.value === days)
    ? String(days)
    : "custom";

  // When comparison mode is active, fetch extra data for the previous period
  const comparisonDays = COMPARISON_OPTIONS.find(o => o.value === comparisonMode)?.days ?? 0;
  const fetchDays = comparisonMode !== 'none' ? days + comparisonDays : days;

  const {
    data: systemData,
    isLoading: systemLoading,
    isError: systemError,
    refetch: refetchSystem,
  } = useSystemSnapshots(fetchDays, showSystem);

  const {
    data: multiLocationData,
    isLoading: locationsLoading,
    isError: locationsError,
    refetch: refetchLocations,
  } = useMultiLocationSnapshots(selectedLocations, fetchDays);

  const isLoading = (showSystem && systemLoading) || (selectedLocations.length > 0 && locationsLoading);
  const isError = (showSystem && systemError) || (selectedLocations.length > 0 && locationsError);

  const refetch = useCallback(() => {
    if (showSystem) refetchSystem();
    if (selectedLocations.length > 0) refetchLocations();
  }, [showSystem, selectedLocations, refetchSystem, refetchLocations]);

  const locationSnapshotsMap = useMemo(() => {
    const result: Record<string, CapacitySnapshot[]> = {};
    if (multiLocationData) {
      for (const [loc, data] of Object.entries(multiLocationData)) {
        result[loc] = data.snapshots;
      }
    }
    return result;
  }, [multiLocationData]);

  // Get snapshots for population breakdown - use system or first selected location
  const breakdownSnapshots = useMemo(() => {
    if (showSystem && systemData?.snapshots) {
      return systemData.snapshots;
    }
    if (selectedLocations.length > 0 && locationSnapshotsMap[selectedLocations[0]]) {
      return locationSnapshotsMap[selectedLocations[0]];
    }
    return [];
  }, [showSystem, systemData, selectedLocations, locationSnapshotsMap]);

  // Determine comparison period label for legend
  const comparisonPeriodLabel = comparisonMode === 'wow' ? 'Previous week' : 
                                 comparisonMode === 'mom' ? 'Previous month' : '';

  const seriesConfigs = useMemo<SeriesConfig[]>(() => {
    const configs: SeriesConfig[] = [];
    
    if (showSystem) {
      // Current period system line (solid)
      configs.push({
        key: "system",
        label: "System-wide",
        color: SERIES_COLORS.system,
        strokeWidth: 3,
      });
      
      // Previous period system line (dashed) - only when comparison is active
      if (comparisonMode !== 'none' && comparisonDays > 0) {
        configs.push({
          key: "system_prev",
          label: `System-wide (${comparisonPeriodLabel})`,
          color: SERIES_COLORS.system,
          strokeWidth: 2,
          strokeDasharray: "6 4",
          isPreviousPeriod: true,
        });
      }
    }
    
    selectedLocations.forEach((loc, index) => {
      const location = locations.find((l) => l.code === loc);
      const color = SERIES_COLORS.locations[index % SERIES_COLORS.locations.length];
      
      // Current period location line
      configs.push({
        key: loc,
        label: location?.label || loc,
        color,
        strokeWidth: 2,
      });
      
      // Previous period location line (dashed) - only when comparison is active
      if (comparisonMode !== 'none' && comparisonDays > 0) {
        configs.push({
          key: `${loc}_prev`,
          label: `${location?.label || loc} (${comparisonPeriodLabel})`,
          color,
          strokeWidth: 2,
          strokeDasharray: "6 4",
          isPreviousPeriod: true,
        });
      }
    });
    
    return configs;
  }, [showSystem, selectedLocations, locations, comparisonMode, comparisonDays, comparisonPeriodLabel]);

  const chartData = useMemo(() => {
    // Use comparison data builder when comparison mode is active
    if (comparisonMode !== 'none' && comparisonDays > 0) {
      return buildComparisonSeriesData(
        systemData?.snapshots,
        locationSnapshotsMap,
        days,
        showSystem,
        selectedLocations,
        comparisonDays
      );
    }
    
    return buildMultiSeriesData(
      systemData?.snapshots,
      locationSnapshotsMap,
      days,
      showSystem,
      selectedLocations
    );
  }, [systemData?.snapshots, locationSnapshotsMap, days, showSystem, selectedLocations, comparisonMode, comparisonDays]);

  const dataPointsWithValues = chartData.filter((d) =>
    seriesConfigs.some((config) => {
      const seriesData = d[config.key] as { hasData: boolean } | undefined;
      return seriesData?.hasData;
    })
  ).length;

  const getLocationLabel = (code: string) => {
    const loc = locations.find((l) => l.code === code);
    return loc?.label || code;
  };

  const hasNoSelection = !showSystem && selectedLocations.length === 0;
  
  // Comparison is only available when viewing system-wide only OR a single location only
  // Disabled when multiple locations are selected (regardless of system-wide toggle)
  const isComparisonAvailable = selectedLocations.length <= 1;
  const comparisonDisabledReason = selectedLocations.length > 1 
    ? "Comparison not available when multiple locations are selected" 
    : null;

  // Auto-reset comparison mode when multiple locations are selected
  useEffect(() => {
    if (selectedLocations.length > 1 && comparisonMode !== 'none') {
      setComparisonMode('none');
    }
  }, [selectedLocations.length, comparisonMode]);

  // Compute comparison statistics (WoW or MoM) using actual snapshot data
  // Works for system-wide OR single location
  const comparisonStats = useMemo(() => {
    if (comparisonMode === 'none') return null;
    if (!isComparisonAvailable) return null;
    
    // Determine which snapshots to use
    let snapshots: CapacitySnapshot[] = [];
    let dataLabel = 'System-wide';
    
    if (showSystem) {
      snapshots = systemData?.snapshots ?? [];
      dataLabel = 'System-wide';
    } else if (selectedLocations.length === 1) {
      const locationCode = selectedLocations[0];
      snapshots = locationSnapshotsMap[locationCode] ?? [];
      dataLabel = getLocationLabel(locationCode);
    }
    
    if (snapshots.length === 0) return null;
    
    const comparisonDays = COMPARISON_OPTIONS.find(o => o.value === comparisonMode)?.days ?? 7;
    
    // Sort snapshots by date descending to find the most recent date
    const sortedSnapshots = [...snapshots].sort(
      (a, b) => new Date(b.snapshotDate).getTime() - new Date(a.snapshotDate).getTime()
    );
    
    if (sortedSnapshots.length === 0) return null;
    
    // Use the most recent snapshot date as the reference point (not new Date())
    const mostRecentDate = new Date(sortedSnapshots[0].snapshotDate);
    mostRecentDate.setHours(23, 59, 59, 999);
    
    const currentPeriodStart = new Date(mostRecentDate);
    currentPeriodStart.setDate(currentPeriodStart.getDate() - comparisonDays + 1);
    currentPeriodStart.setHours(0, 0, 0, 0);
    
    const previousPeriodEnd = new Date(currentPeriodStart);
    previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);
    previousPeriodEnd.setHours(23, 59, 59, 999);
    
    const previousPeriodStart = new Date(previousPeriodEnd);
    previousPeriodStart.setDate(previousPeriodStart.getDate() - comparisonDays + 1);
    previousPeriodStart.setHours(0, 0, 0, 0);
    
    // Filter snapshots into current and previous periods
    const currentPeriodSnapshots = sortedSnapshots.filter(s => {
      const date = new Date(s.snapshotDate);
      return date >= currentPeriodStart && date <= mostRecentDate;
    });
    const previousPeriodSnapshots = sortedSnapshots.filter(s => {
      const date = new Date(s.snapshotDate);
      return date >= previousPeriodStart && date <= previousPeriodEnd;
    });
    
    if (currentPeriodSnapshots.length === 0 && previousPeriodSnapshots.length === 0) return null;
    
    // Calculate averages for each period
    const avgCurrent = currentPeriodSnapshots.length > 0 
      ? currentPeriodSnapshots.reduce((sum, s) => sum + s.occupancyAllKennels, 0) / currentPeriodSnapshots.length 
      : 0;
    const avgPrevious = previousPeriodSnapshots.length > 0 
      ? previousPeriodSnapshots.reduce((sum, s) => sum + s.occupancyAllKennels, 0) / previousPeriodSnapshots.length 
      : 0;
    
    const change = avgCurrent - avgPrevious;
    const percentChange = avgPrevious > 0 ? (change / avgPrevious) * 100 : 0;
    
    return {
      currentAvg: Math.round(avgCurrent),
      previousAvg: Math.round(avgPrevious),
      change: Math.round(change),
      percentChange: percentChange.toFixed(1),
      periodLabel: comparisonMode === 'wow' ? 'week' : 'month',
      currentDataPoints: currentPeriodSnapshots.length,
      previousDataPoints: previousPeriodSnapshots.length,
      dataLabel,
    };
  }, [comparisonMode, systemData?.snapshots, showSystem, selectedLocations, locationSnapshotsMap, isComparisonAvailable, getLocationLabel]);


  const chartsReady = !hasNoSelection && !isLoading && !isError;

  return (
    <div ref={trendsCardRef} className="space-y-6" data-testid="card-capacity-trends">
      <div
        ref={controlsRef}
        className="px-4 py-3 border rounded-lg bg-background sticky z-30 shadow-sm space-y-2"
        style={{ top: `${stickyTop}px` }}
        data-testid="trends-shared-controls"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <TrendingUp className="h-5 w-5 text-muted-foreground shrink-0" />
          <h3 className="text-xl font-semibold leading-none tracking-tight" data-testid="text-trends-heading">Population Trends</h3>
          {isStale && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 text-amber-500 text-xs cursor-help">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>Stale</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium mb-1">Stale locations:</p>
                {staleLocationNames.length > 0 ? (
                  <ul className="list-disc pl-4">
                    {staleLocationNames.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                ) : (
                  <p>Some locations have stale data</p>
                )}
              </TooltipContent>
            </Tooltip>
          )}
          {chartsReady && (
            <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1 shrink-0">
              <Info className="h-3 w-3" />
              {dataPointsWithValues} pt{dataPointsWithValues !== 1 ? "s" : ""}
              {chartData.length > 0 && dataPointsWithValues < chartData.length && (
                <span className="text-muted-foreground/70">
                  ({chartData.length - dataPointsWithValues} gap{chartData.length - dataPointsWithValues !== 1 ? "s" : ""})
                </span>
              )}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground" data-testid="text-trends-description">
          Utilization, headcount, and composition over time. Filter by location or compare periods.
        </p>

        <div className="flex items-center gap-2 flex-wrap pt-1">
          <Badge
            variant={showSystem ? "default" : "outline"}
            className={`cursor-pointer gap-1.5 text-xs ${showSystem ? "" : "text-muted-foreground"}`}
            onClick={() => setShowSystem(!showSystem)}
            data-testid="chip-system-wide"
          >
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SERIES_COLORS.system }} />
            System
          </Badge>

          <div className="w-px h-4 bg-border shrink-0" />

          {locations.map((loc) => {
            const isSelected = selectedLocations.includes(loc.code);
            const selectedIndex = selectedLocations.indexOf(loc.code);
            const isDisabled = !isSelected && selectedLocations.length >= MAX_LOCATIONS;
            const chipColor = isSelected
              ? SERIES_COLORS.locations[selectedIndex % SERIES_COLORS.locations.length]
              : undefined;

            return (
              <Badge
                key={loc.code}
                variant={isSelected ? "default" : "outline"}
                className={`cursor-pointer gap-1.5 text-xs ${
                  isDisabled ? "opacity-40 cursor-not-allowed" : ""
                } ${!isSelected ? "text-muted-foreground" : ""}`}
                onClick={() => !isDisabled && toggleLocation(loc.code)}
                data-testid={`chip-location-${loc.code}`}
              >
                {isSelected && chipColor && (
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: chipColor }} />
                )}
                {loc.label}
                {isSelected && (
                  <X className="h-3 w-3 ml-0.5 opacity-60" />
                )}
              </Badge>
            );
          })}

          <div className="w-px h-4 bg-border shrink-0" />

          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Select
                  value={comparisonMode}
                  onValueChange={(v) => setComparisonMode(v as ComparisonMode)}
                  disabled={!isComparisonAvailable}
                >
                  <SelectTrigger
                    className={`h-7 w-[100px] text-xs ${!isComparisonAvailable ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="select-comparison-mode"
                  >
                    <Grid3X3 className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <SelectValue placeholder="Compare" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPARISON_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger>
            {comparisonDisabledReason && (
              <TooltipContent side="bottom">
                <p className="text-xs">{comparisonDisabledReason}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </div>

      {hasNoSelection ? (
        <div className="flex items-center justify-center h-[300px] border rounded-lg bg-muted/20">
          <div className="text-center">
            <Filter className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No trends selected</p>
            <p className="text-sm text-muted-foreground mt-1">
              Enable System-wide or select locations to view trends
            </p>
            <Button variant="outline" size="sm" className="mt-4" onClick={showSystemOnly}>
              Show System-wide Trends
            </Button>
          </div>
        </div>
      ) : isLoading ? (
        <div className="h-[400px] flex flex-col items-center justify-center border rounded-lg bg-muted/10" data-testid="chart-loading">
          <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin mb-3" />
          <p className="text-muted-foreground font-medium">Loading chart data...</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Fetching population trends</p>
        </div>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load trend data</AlertTitle>
          <AlertDescription className="flex items-center gap-2 mt-2">
            <span>Unable to fetch capacity snapshots.</span>
            <Button variant="outline" size="sm" onClick={refetch} data-testid="button-retry-trends">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {renderHint?.("capacity-utilization")}
          <Card id="section-capacity-utilization" data-testid="card-capacity-utilization">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Capacity Utilization</CardTitle>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="show-occupancy"
                      checked={showOccupancy}
                      onCheckedChange={setShowOccupancy}
                      data-testid="switch-show-occupancy"
                    />
                    <Label htmlFor="show-occupancy" className="text-sm cursor-pointer">
                      Show occupancy
                    </Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          data-testid="button-occupancy-info"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs">
                        <p className="font-semibold mb-1">Occupancy in tooltip</p>
                        <ul className="text-xs space-y-1">
                          <li>
                            <strong>Utilization %</strong> shows how full we are relative to{" "}
                            <strong>kennel capacity limits</strong>.
                          </li>
                          <li>
                            <strong>Occupancy</strong> shows the <strong>raw animal count</strong> used to calculate that
                            utilization (only animals in kennels <strong>with limits</strong>).
                          </li>
                          <li>If capacity limits change, utilization % can change even if occupancy stays the same.</li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <div className="h-0.5 w-4 border-t border-dashed border-destructive/50" />
                    <span>85% threshold</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <CapacityChart data={chartData} seriesConfigs={seriesConfigs} showOccupancy={showOccupancy} />
              <TrendsLegend seriesConfigs={seriesConfigs} />
            </CardContent>
          </Card>

          {renderHint?.("kennel-population")}
          <Card id="section-kennel-population" data-testid="card-kennel-population">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Kennel Population</CardTitle>
            </CardHeader>
            <CardContent>
              <PopulationChart data={chartData} seriesConfigs={seriesConfigs} />
              <TrendsLegend seriesConfigs={seriesConfigs} />
            </CardContent>
          </Card>

          {renderHint?.("population-breakdown")}
          <Card id="section-population-breakdown" data-testid="card-population-breakdown">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Population Breakdown</CardTitle>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={breakdownMetric} onValueChange={(v) => setBreakdownMetric(v as TrendMetric)}>
                    <SelectTrigger className="w-[140px]" data-testid="select-breakdown-metric">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="species">Species</SelectItem>
                      <SelectItem value="kennel">Kennel Status</SelectItem>
                      <SelectItem value="eval">Behavior Eval</SelectItem>
                      <SelectItem value="adoption">Adoption Level</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <BreakdownComparisonGrid
                showSystem={showSystem}
                systemSnapshots={systemData?.snapshots ?? []}
                selectedLocations={selectedLocations}
                locationSnapshotsMap={locationSnapshotsMap}
                getLocationLabel={getLocationLabel}
                metric={breakdownMetric}
                days={days}
                isLoading={isLoading}
              />
            </CardContent>
          </Card>

          {renderHint?.("kennel-status-trends")}
          <KennelStatusTrendsChart
            days={days}
            showSystem={showSystem}
            selectedLocations={selectedLocations}
            comparisonMode={comparisonMode}
            locations={locations}
          />

          {renderHint?.("adoption-pipeline")}
          <AdoptionPipelineChart
            days={days}
            showSystem={showSystem}
            selectedLocations={selectedLocations}
            comparisonMode={comparisonMode}
            locations={locations}
          />
        </>
      )}

    </div>
  );
}
